import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useReducer,
  useRef,
  useEffect
} from 'react';
import FormBuilderCustomButtonsView from '../view/FormBuilderCustomButtonsView';
import { AutomationsContex } from './FormBuilderAutomationsEngine';
import Animation from '../Animation';
import CustomButtonWizardEngine from './CustomButtonWizardEngine';
import { FormBuilderContext } from './FormBuilderEngine';
import Colors from '../../internal-components/Colors';
import SerializationCopy from '../../utils/SerializationCopy';
import useSkipUpdate from '../../hooks/useSkipRender';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import fetchAPI from '../../utils/fetchAPI';
import { UPDATE_FORM } from '../../store/modules/state/formBuilder';
import useTranslations from '../../hooks/useTranslations';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import { replaceElements } from './FormBuilderStatusesEngine';

export const BUTTON_TYPE = {
  END_USER: 'eu',
  ADMIN: 'admin',
  BOTH: 'both'
};

// maybe need to change the ids based on backed
export const SYSTEM_BUTTONS_DEFAULT_SCHEMA = {
  SAVE_CHANGES: {
    id: 'SAVE_CHANGES',
    label: 'systemBtn.label.saveChanges',
    variant: 'contained',
    color: Colors.PRIMARY,
    type: BUTTON_TYPE.ADMIN,
    statuses: 'all'
  },
  SUBMIT: {
    id: 'SUBMIT',
    label: 'systemBtn.label.submit',
    variant: 'contained',
    color: Colors.PRIMARY,
    type: BUTTON_TYPE.END_USER,
    statuses: 'all'
  },
  SIGN_AND_SUBMIT: {
    id: 'SIGN_AND_SUBMIT',
    label: 'forms.docusign',
    variant: 'contained',
    color: Colors.PRIMARY,
    type: BUTTON_TYPE.END_USER,
    statuses: 'all'
  },
  REQUEST_MORE_INFO: {
    id: 'REQUEST_MORE_INFO',
    label: 'systemBtn.label.request_more_info',
    variant: 'outlined',
    color: Colors.PRIMARY,
    type: BUTTON_TYPE.ADMIN,
    statuses: 'all'
  }
};

export const INTEGRATION_BUTTONS_DEFAULT_SCHEMA = {
  SUBMIT_AND_THIRDFORT: {
    id: 'SUBMIT_AND_THIRDFORT',
    label: 'forms.thirdfort',
    variant: 'contained',
    color: Colors.PRIMARY,
    type: BUTTON_TYPE.BOTH,
    statuses: 'all'
  }
};

export const BUTTONS_THAT_CANT_BE_DISABLED = ['SUBMIT_AND_THIRDFORT'];

export const ACTIVE_VIEW = {
  LIST_OF_BUTTONS: 'lob',
  BUTTON_WIZARD: 'bw'
};

export const BUTTON_CATEGORIES = {
  SYSTEM: 'system',
  INTEGRATION: 'integration',
  CUSTOM: 'custom'
};

export const getButtonCategory = btn => {
  if (
    Object.values(SYSTEM_BUTTONS_DEFAULT_SCHEMA).find(
      defSchBtn => btn.id === defSchBtn.id
    )
  ) {
    return BUTTON_CATEGORIES.SYSTEM;
  } else if (
    Object.values(INTEGRATION_BUTTONS_DEFAULT_SCHEMA).find(
      defSchBtn => btn.id === defSchBtn.id
    )
  ) {
    return BUTTON_CATEGORIES.INTEGRATION;
  } else {
    return BUTTON_CATEGORIES.CUSTOM;
  }
};

const sortBtns = (a, b) => {
  if (a.position === undefined || !b.position === undefined) {
    /**
     * Prior to https://vinciworksteam.atlassian.net/browse/O20-3655 we didn't have 'position'
     * property on a button entity.
     * If we didn't find a position on a button it MUST mean that all other buttons won't have
     * position as well, therefore we should keep original order.
     */
    return 0;
  }

  return a.position - b.position;
};

/**
 * Merge arrays and sort them
 * @param {Array} systemButtons
 * @param {Array} customButtons
 * @param {Array} integrationButtons
 */
export const mergeButtons = (
  systemButtons,
  customButtons,
  integrationButtons
) => {
  const allButtons = [
    ...systemButtons,
    ...customButtons,
    ...integrationButtons
  ];

  allButtons.sort(sortBtns);

  return allButtons.map((btn, i) => ({ position: i, ...btn }));
};

/**
 * Split all buttons into two arrays - systemButtons and customButtons
 * @param {Array} allButtons
 *
 * @return {Array} {systemButtons: [...], customButtons: [...]}
 */
export const splitButtons = allButtons => {
  const systemButtons = [],
    customButtons = [],
    integrationButtons = [];

  allButtons.forEach(btn => {
    const category = getButtonCategory(btn);
    switch (category) {
      case BUTTON_CATEGORIES.SYSTEM:
        systemButtons.push(btn);
        break;
      case BUTTON_CATEGORIES.INTEGRATION:
        integrationButtons.push(btn);
        break;
      case BUTTON_CATEGORIES.CUSTOM:
      default:
        customButtons.push(btn);
        break;
    }
  });

  return {
    systemButtons,
    customButtons,
    integrationButtons
  };
};

/**
 *
 * This function will check for integration (only Docusign at the moment) and decide which systme buttons should show.
 * For e.g if docusign is enambled we'll wantt o filter the regular submit button and if it's disabled we'll want to filter the sign and submit button
 *
 * @param {Array} systemButtons - array of system buttons to filter
 * @param {Function} setIsLoading - loader hook to set
 * @returns function to filter on systemButtons array
 */
export const getButtonsFilterFunction = async formId => {
  const isDocusignEnabled = await fetchAPI(
    `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"},{"field":"'integrationType'","op":"=","value":"docusign"},{"field":"'active'","op":"=","value":"true"}]]`
  ).then(r => r.results && !!r.results.length);

  const isThirdfortEnabled = await fetchAPI(
    `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"},{"field":"'integrationType'","op":"=","value":"thirdfort"},{"field":"'active'","op":"=","value":"true"}]]`
  ).then(r => r.results && !!r.results.length);

  const isWorldcheckEnabled = await fetchAPI(
    `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"},{"field":"'integrationType'","op":"=","value":"worldcheck"},{"field":"'active'","op":"=","value":"true"}]]`
  ).then(r => r.results && !!r.results.length);

  const isCredasEnabled = await fetchAPI(
    `/scheduler/v1/integration?query=[[{"field":"'assetId'","op":"=","value":"${formId}"},{"field":"'integrationType'","op":"=","value":"credas"},{"field":"'active'","op":"=","value":"true"}]]`
  ).then(r => r.results && !!r.results.length);

  let ids2Filter = [];

  if (isDocusignEnabled) {
    ids2Filter.push(SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id);
  } else {
    ids2Filter.push(SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id);
  }

  if (!isThirdfortEnabled && !isWorldcheckEnabled && !isCredasEnabled) {
    ids2Filter.push(INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id);
  }

  return sb => ids2Filter.indexOf(sb.id) === -1;
};

export const CustomButtonsContext = createContext({});

const CustomButtonsReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialReducerState = {
  // systemButtons: [],
  // customButtons: []
  buttons: []
};

const FormBuilderCustomButtonsEngine = props => {
  const { form, reduxDispatch } = useContext(FormBuilderContext);
  const isDataInitiated = useRef(false);
  const [data, dispatch] = useReducer(
    CustomButtonsReducer,
    initialReducerState
  );
  const [wizardBtnData, setWizardBtnData] = useState(undefined); // importand - we nullify this with undefined value
  const [activeView, setActiveView] = useState(ACTIVE_VIEW.LIST_OF_BUTTONS);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [btn2Delete, setBtn2Delete] = useState('');
  const btnIdInDeleteReview = useRef(''); // saves the button id that is requested to be deleted while the delete dialog is showing
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { setIsOTLoading } = useGlobalVariables();
  const t = useTranslations();
  const filterFunctionRef = useRef(() => true);

  const initData = useCallback(async () => {
    const formButtons = form.buttons || {};
    let systemButtons = formButtons.systemButtons;
    let integrationButtons = formButtons.integrationButtons;

    if (!systemButtons || !systemButtons.length) {
      // no changes made to system buttons, use default schema
      systemButtons = Object.values(SYSTEM_BUTTONS_DEFAULT_SCHEMA);
    } else {
      // form has data regarding system buttons
      systemButtons = Object.values(SYSTEM_BUTTONS_DEFAULT_SCHEMA).map(
        sysBtn => {
          // iterate over system buttons and check if there is a
          // pre-defined configurations for it on the form schema
          const modifiedSysBtn = systemButtons.find(b => sysBtn.id === b.id);
          if (modifiedSysBtn) {
            // found pre-defind configuration - use it
            return { ...sysBtn, ...modifiedSysBtn };
          }
          // didn't find configuration - use system definition
          return sysBtn;
        }
      );
    }

    if (!integrationButtons || !integrationButtons.length) {
      integrationButtons = Object.values(INTEGRATION_BUTTONS_DEFAULT_SCHEMA);
    } else {
      // form has data regarding integration buttons
      integrationButtons = Object.values(
        INTEGRATION_BUTTONS_DEFAULT_SCHEMA
      ).map(intBtn => {
        // iterate over integration buttons and check if there is a
        // pre-defined configurations for it on the form schema
        const modifiedIntBtn = integrationButtons.find(b => intBtn.id === b.id);
        if (modifiedIntBtn) {
          // found pre-defind configuration - use it
          return { ...intBtn, ...modifiedIntBtn };
        }
        // didn't find configuration - use integration definition
        return intBtn;
      });
    }

    setIsOTLoading(true);
    filterFunctionRef.current = await getButtonsFilterFunction(form.id);
    setIsOTLoading(false);

    const customButtons =
      (form.buttons && (form.buttons.customButtons || [])) || [];
    const allButtons = mergeButtons(
      systemButtons,
      customButtons,
      integrationButtons
    );

    skipUpdate();
    dispatch({ type: 'update', payload: { buttons: allButtons } });
  }, [form]);

  useEffect(() => {
    if (shouldSkipUpdate()) {
      return;
    }

    const updateFormSchemaViaAPI = () => {
      const allStatuses = [
        'all',
        'new',
        ...form.customStatuses.map(cs => cs.internalId)
      ];

      const buttons = splitButtons(data.buttons);
      buttons.systemButtons.forEach(sB => {
        if (sB.statuses === 'all') {
          sB.statuses = allStatuses;
        }
      });
      buttons.integrationButtons.forEach(iB => {
        if (iB.statuses === 'all') {
          iB.statuses = allStatuses;
        }
      });
      fetchAPI(`/forms/v1/forms/${form.id}`, {
        method: 'PUT',
        body: { ...form, buttons }
      })
        .then(() => {
          // changes saved
          openSnackbar('success', 'automation.snackbar.success.general');

          reduxDispatch({
            type: UPDATE_FORM,
            payload: { ...form, buttons }
          });
        })
        .catch(e => openSnackbar('error', 'customButtons.snackbar.fail.save'));
    };

    updateFormSchemaViaAPI();
  }, [data]);

  useEffect(() => {
    if (!form || !Object.keys(form).length || isDataInitiated.current) {
      return;
    }
    isDataInitiated.current = true;
    initData();
  }, [form]);

  const onWizardOpned = useCallback(btnData => {
    setWizardBtnData(btnData);
    setActiveView(ACTIVE_VIEW.BUTTON_WIZARD);
  }, []);

  const onWizardClosed = useCallback(view => {
    setWizardBtnData(undefined);
    setActiveView(view || ACTIVE_VIEW.LIST_OF_BUTTONS);
  }, []);

  const updateSingleButton = useCallback(
    buttonData => {
      const buttons = SerializationCopy(data.buttons);
      const index = buttons.findIndex(b => b.id === buttonData.id);

      if (index === -1) {
        buttonData.position = buttons.length;
        buttons.push(buttonData);
      } else {
        buttons[index] = buttonData;
      }

      dispatch({
        type: 'update',
        payload: {
          buttons
        }
      });
    },
    [data]
  );

  const initButtonDeleteProccess = useCallback(
    buttonId => {
      const automations = form.automations || [];

      const automationUsingBtn = automations.find(
        a =>
          a.rule &&
          a.rule.when &&
          a.rule.when.eventType === 'cb' &&
          a.rule.when.eventInfo === buttonId
      );

      if (automationUsingBtn) {
        openSnackbar(
          'error',
          t('customButtons.snackbar.error.delete.usedInAutomation')
        );
        return;
      }

      btnIdInDeleteReview.current = buttonId;
      setIsDeleteDialogOpen(true);
    },
    [data, form]
  );

  const onButtonDelete = useCallback(
    buttonId => {
      const buttons = SerializationCopy(data.buttons);
      const updatedButtons = buttons.filter(cb => cb.id !== buttonId);

      dispatch({
        type: 'update',
        payload: {
          buttons: updatedButtons
        }
      });
      setBtn2Delete('');
    },
    [data]
  );

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      if (result.destination.index === result.source.index) {
        return;
      }
      const previousIndex = result.source.index;
      const newIndex = result.destination.index;
      const buttons = replaceElements(
        data.buttons,
        previousIndex,
        newIndex
      ).map((btn, i) => ({ ...btn, position: i }));
      buttons.sort(sortBtns);
      dispatch({
        type: 'update',
        payload: { buttons }
      });
    },
    [data]
  );

  const viewProps = {
    buttons: data.buttons.filter(filterFunctionRef.current),
    setActiveView,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    btnIdInDeleteReviewRef: btnIdInDeleteReview,
    setBtn2Delete,
    onDragEnd
  };

  const wizardProps = { buttonData: wizardBtnData, save: updateSingleButton };
  const contextProps = {
    setActiveView,
    onWizardOpned,
    onWizardClosed,
    btn2Delete,
    initButtonDeleteProccess,
    onButtonDelete,
    updateSingleButton
  };

  return (
    <CustomButtonsContext.Provider value={contextProps}>
      <Animation
        appear
        type="collapse"
        in={activeView === ACTIVE_VIEW.LIST_OF_BUTTONS}
      >
        <FormBuilderCustomButtonsView {...viewProps} />
      </Animation>

      <Animation
        type="collapse"
        in={activeView === ACTIVE_VIEW.BUTTON_WIZARD}
        mountOnEnter
        unmountOnExit
      >
        <CustomButtonWizardEngine {...wizardProps} />
      </Animation>
    </CustomButtonsContext.Provider>
  );
};

export default FormBuilderCustomButtonsEngine;
